import { useEffect, useState, useMemo } from 'react';
import { IProductPrice, Product } from '@/types';
import { Image } from '@/components/ui/image';
import { useCompareFunctions } from '@/lib/hooks/use-compare';
import { formatPrice } from '@/lib/use-price';
import SquaredBox from '@/components/ui/squared-box';
import { CoinsIcon } from '@/components/icons/product/coins-icon';

export default function ProductCompare({ products }: Readonly<{ products: Product[] }>) {
  const { loadCompareInfo } = useCompareFunctions();

  const [compareList, setCompareList] = useState<
    { id: string; image: string; value: number }[]
  >([]);
  const [economiaMedia, setEconomiaMedia] = useState(0);
  const [hasCompareList, setHasCompareList] = useState(false);

  useEffect(() => {
    const first = products[0] as Product & { product_prices?: IProductPrice[] };
    const prices = first?.prices ?? first?.product_prices;
    if (!products.length || !prices?.length) return;

    const infos = loadCompareInfo(products);
    if (!infos.length) return;

    const info = infos[0];
    if (!info?.payload) return;

    setCompareList(info.payload);
    setEconomiaMedia(info.economiaMedia);
    setHasCompareList(info.hasCompareList);
  }, [products, loadCompareInfo]);

  const hasItemsToShow = useMemo(
    () => hasCompareList && compareList.some((el) => el.value > 0),
    [hasCompareList, compareList],
  );

  if (!hasItemsToShow) return null;

  return (
    <SquaredBox>
      <SquaredBox.Header
        title="Compare e comprove"
        titleStyle="text-red-500 font-semibold"
        subtitle="Veja o melhor preço, o nosso"
        icon={<CoinsIcon />}
        className="bg-[rgba(240,86,122,0.1)]"
      />
      <SquaredBox.Content className="items-center px-2">
        <div className="w-full py-[12px] px-0 md:p-[10px] md:px-0">
          <ul className="list-none space-y-1">
            {compareList.map(({ value, id, image }) => (
              <li className="flex items-center justify-between" key={id}>
                <div>
                  <Image
                    src={image}
                    alt="Concorrente"
                    width={180}
                    height={30}
                    unoptimized={true}
                  />
                </div>
                {value > 0 ? (
                  <div className="flex items-center">
                    <span className="h-[18px] text-base font-semibold text-gray-200">
                      {formatPrice({ amount: value })}
                    </span>
                  </div>
                ) : (
                  <div>
                    <span className="font-bold text-red-200">Indisponível</span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>

        <p className="flex justify-center text-[16px] p-[15px_30px_18px_26px] text-accent md:text-[20px]">
          Economia média{' '}
          <strong className="ml-[5px]">
            {formatPrice({ amount: economiaMedia })}
          </strong>
        </p>
      </SquaredBox.Content>
    </SquaredBox>
  );
}
